import { Box, Dialog, DialogContent, Typography, Stack, useTheme } from '@mui/material';
import Button from './Button';
import { MdOutlineWarningAmber } from 'react-icons/md';
import { IoFlashOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { agency, business } from '@/routes/config';
import Cookies from 'js-cookie';

interface PaymentCardWarningModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly billingType?: 'agency' | 'business';
}

const COOKIE_NAME = 'payment_card_reminder_dismissed';

export default function PaymentCardWarningModal({
  open,
  onClose,
  billingType = 'agency'
}: PaymentCardWarningModalProps) {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleConnectCard = () => {
    Cookies.set(COOKIE_NAME, 'true', { expires: 1 });
    const billingRoute =
      billingType === 'agency' ? `${agency.base}/${agency.billing}` : `${business.base}/${business.billing}`;
    navigate(billingRoute);
    onClose();
  };

  const handleRemindTomorrow = () => {
    Cookies.set(COOKIE_NAME, 'true', { expires: 1 });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        onClose();
      }}
      maxWidth='sm'
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            p: 2
          }
        }
      }}
    >
      <DialogContent sx={{ p: 3 }}>
        <Stack alignItems='center'>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: '#FEE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <MdOutlineWarningAmber size={48} color='#D32F2F' />
          </Box>

          <Typography variant='h5' mt={3} fontSize='20px' fontWeight={500} textAlign='center'>
            Payment Card Required
          </Typography>

          <Typography variant='body1' mt={1} fontSize='14px' textAlign='center' color='text.secondary'>
            Please add a payment card to continue using our services.
          </Typography>

          <Box
            mt={3}
            sx={{
              width: '100%',
              bgcolor: '#FFF2F2',
              borderRadius: 2,
              px: 2,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              justifyContent: 'center'
            }}
          >
            <IoFlashOutline size={18} color='error' />
            <Typography variant='body2' color='error' fontWeight={400}>
              Service will be stopped without payment method
            </Typography>
          </Box>

          <Stack direction='row' spacing={2} width='100%' mt={4}>
            <Button
              variant='outlined'
              color='inherit'
              onClick={handleRemindTomorrow}
              fullWidth
              sx={{
                borderColor: theme.palette.grey[300],
                color: theme.palette.text.primary,
                '&:hover': {
                  borderColor: theme.palette.grey[400],
                  bgcolor: theme.palette.grey[50]
                }
              }}
            >
              Remind Me Tomorrow
            </Button>
            <Button variant='contained' color='primary' onClick={handleConnectCard} fullWidth>
              Connect Payment Card
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export { COOKIE_NAME as PAYMENT_CARD_REMINDER_COOKIE };
